/**
 * Ganchos de la capa offline.
 *
 * Todo lo que la interfaz necesita saber sobre la red y la cola de salida:
 * si hay conexión, cuántas ventas están pendientes de sincronizar, y cómo se
 * llena la caché del catálogo.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { listProducts } from "../endpoints";
import { getSession, onSessionChange } from "../session";
import {
  clearTenantLocalCache,
  db,
  getMeta,
  outboxSummary,
  replaceCatalog,
  setMeta,
  type CachedProduct,
} from "./db";
import { normalize } from "./db";
import { syncOutbox } from "./outbox";

/** ¿Hay red? Se actualiza sola cuando el navegador lo detecta. */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  );

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);

  return online;
}

/** Cuántas operaciones esperan a sincronizarse y cuántas fueron rechazadas. */
export function useOutbox() {
  const queryClient = useQueryClient();

  const summary = useQuery({
    queryKey: ["outbox"],
    queryFn: outboxSummary,
    refetchInterval: 3_000,
  });

  const sync = useMutation({
    mutationFn: syncOutbox,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["outbox"] });
      void queryClient.invalidateQueries({ queryKey: ["sales"] });
    },
  });

  return {
    pending: summary.data?.pending ?? 0,
    rejected: summary.data?.rejected ?? 0,
    sync: () => sync.mutate(),
    syncing: sync.isPending,
  };
}

/**
 * Llena la caché del catálogo para poder vender sin red.
 *
 * Se ejecuta al entrar, al cambiar de negocio y cada vez que la pestaña
 * recupera el foco. Si el `tenant.id` cambió respecto al que estaba guardado
 * en IndexedDB, limpia inmediatamente la caché local antes de descargar.
 */
export function useCatalogSync(): { syncedAt: number | null; products: number } {
  const [syncedAt, setSyncedAt] = useState<number | null>(null);
  const [products, setProducts] = useState(0);
  const [activeTenantId, setActiveTenantId] = useState<string | null>(
    () => getSession()?.tenant?.id ?? null,
  );
  const online = useOnlineStatus();

  useEffect(() => {
    return onSessionChange((s) => {
      setActiveTenantId(s?.tenant?.id ?? null);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function syncCatalog() {
      const session = getSession();
      if (!session) {
        await clearTenantLocalCache();
        if (!cancelled) {
          setSyncedAt(null);
          setProducts(0);
        }
        return;
      }

      const cachedTenantId = await getMeta<string>("catalog.tenantId");
      if (cachedTenantId && cachedTenantId !== session.tenant.id) {
        await clearTenantLocalCache();
      }

      if (!online) return;
      try {
        const cache: CachedProduct[] = [];
        let offset = 0;
        // Se pagina hasta traer todo el catálogo: 200 por página es un buen
        // equilibrio entre número de peticiones y tamaño de cada una.
        for (;;) {
          const page = await listProducts({ limit: 200, offset });
          for (const product of page.items) {
            for (const variant of product.variants ?? []) {
              cache.push({
                variantId: variant.id,
                productId: product.id,
                sku: variant.sku,
                name: variant.name,
                productName: product.name,
                kind: product.kind,
                barcode: variant.barcodes?.[0]?.barcode ?? null,
                priceCents: variant.price_cents ?? null,
                tracked: product.track_inventory,
                taxRate: product.tax_rate_code ?? "",
                searchText: normalize(`${product.name} ${variant.name} ${variant.sku}`),
              });
            }
          }
          offset += page.items.length;
          if (offset >= page.total || page.items.length === 0) break;
        }

        await replaceCatalog(cache, session.tenant.id);
        const stored = await getMeta<number>("catalog.syncedAt");
        if (!cancelled) {
          setSyncedAt(stored ?? Date.now());
          setProducts(cache.length);
        }
      } catch {
        // Sin red o API caída: se sigue con la caché que ya hubiera si es del mismo tenant.
        const stored = await getMeta<number>("catalog.syncedAt");
        if (!cancelled) {
          setSyncedAt(stored ?? null);
          setProducts(await db.catalog.count());
        }
      }
    }

    void syncCatalog();
    const onFocus = () => void syncCatalog();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
    };
  }, [online, activeTenantId]);

  return { syncedAt, products };
}

/** Guarda la sucursal activa para saber dónde se está vendiendo. */
export async function rememberBranch(branchId: string): Promise<void> {
  await setMeta("pos.branchId", branchId);
}

export async function rememberedBranch(): Promise<string | undefined> {
  return getMeta<string>("pos.branchId");
}
